import React, { Fragment, useEffect, useState } from 'react';
import {
  Box,
  Card,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { GIQuestion } from 'views/App/Portal/Policy/_types/giq';
import { translate as t } from 'hooks/useTranslations';
import { Edit, InfoOutlined } from '@mui/icons-material';
import { neutral } from '_lex/_foundations/phxColors';
import _ from 'lodash';
import { isOtherValue } from '../helpers';

interface QuestionItemProps {
  answerable: boolean;
  answerQuestion: (questionId: string, value: string) => void;
  editable: boolean;
  question: GIQuestion;
  setEditQuestionId: React.Dispatch<React.SetStateAction<string | null>>;
}

const QuestionItem: React.FC<QuestionItemProps> = ({
  answerable,
  answerQuestion,
  editable,
  question,
  setEditQuestionId,
}) => {
  const isOtherAnswer =
    question.questionType === 'SINGLE_SELECTION' &&
    !_.isEmpty(question.answer) &&
    (
      !question.choices?.map((c) => c.value).includes(question.answer ?? '') ||
      isOtherValue(question.answer)
    );

  // If the stored answer is just a variation of "other" (e.g., "Other:", "other", "Other (lowercase):"),
  // clear it so the user can enter an actual value
  const getInitialOtherAnswer = () => {
    if (!isOtherAnswer || !question.answer) return '';
    if (isOtherValue(question.answer)) return '';
    return question.answer;
  };

  const [currentAnswer, setCurrentAnswer] = useState<string>(isOtherAnswer ? 'OTHER' : (question.answer ?? ''));
  const [otherAnswer, setOtherAnswer] = useState<string>(getInitialOtherAnswer());

  useEffect(() => {
    if (currentAnswer && currentAnswer !== question.answer) {
      let a = currentAnswer;
      if (a === 'OTHER') a = otherAnswer;
      answerQuestion(question.id!, a);
    }
  }, [currentAnswer, otherAnswer, question.answer, question.id]);

  return (
    <Card
      className='phx-card'
      id={`question_${question.id}`}
      sx={{
        '.edit-btn': { visibility: 'hidden' },
        '&:hover .edit-btn': { visibility: 'visible' },
      }}
    >
      <Stack spacing={4}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: 4,
          }}
        >
          <Stack>
            <Typography
              variant='overline'
              sx={{
                lineHeight: '1em',
                color: neutral[500],
              }}
            >
              {t('draftManuals.giq.list.textEntity')}
            </Typography>
            <Typography variant='subtitle1'>{question.textEntity?.textEntity}</Typography>
          </Stack>
          <Box>
            {editable && (
              <IconButton
                className='edit-btn'
                onClick={() => setEditQuestionId(question.id!)}
              >
                <Edit />
              </IconButton>
            )}
          </Box>
        </Box>
        <Stack>
          <Typography variant='subtitle1'>{question.question}</Typography>
          {answerable ? (
            <>
              {question.questionType === 'CUSTOM_INPUT' ? (
                <TextField
                  id={`question_${question.id}_answer`}
                  key={`question_${question.id}_answer`}
                  label={false}
                  variant='outlined'
                  fullWidth
                  defaultValue={question.answer}
                  onChange={({ target }) => setCurrentAnswer(target.value)}
                  sx={{ p: 0 }}
                />
              ) : (
                <>
                  <RadioGroup
                    defaultValue={isOtherAnswer ? 'OTHER' : question.answer}
                    id={`question_${question.id}_answer`}
                    key={`question_${question.id}_answer`}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setCurrentAnswer((event.target as HTMLInputElement).value);
                    }}
                    sx={{ pl: 5 }}
                  >
                    {question.choices?.map((choice) => (
                      <Fragment key={`question_${question.id}_${choice.value}`}>
                        {isOtherValue(choice.value) ? (
                          <FormControlLabel
                            value='OTHER'
                            control={<Radio />}
                            label={
                              <Stack
                                direction='row'
                                spacing={2}
                                sx={{ alignItems: 'center' }}
                              >
                                <Typography>Other</Typography>
                                <TextField
                                  id={`question_${question.id}_other_answer`}
                                  key={`question_${question.id}_other_answer`}
                                  label={false}
                                  size='small'
                                  variant='outlined'
                                  fullWidth
                                  value={otherAnswer}
                                  onChange={({ target }) => setOtherAnswer(target.value)}
                                  sx={{ minWidth: 200, p: 0 }}
                                  disabled={currentAnswer !== 'OTHER'}
                                />
                              </Stack>
                            }
                          />
                        ) : (
                          <FormControlLabel
                            value={choice.value}
                            control={<Radio />}
                            label={choice.value}
                          />
                        )}
                      </Fragment>
                    ))}
                  </RadioGroup>
                </>
              )}
            </>
          ) : (
            <>
              {question.choices && (
                <Stack
                  spacing={2}
                  sx={{ pl: 5 }}
                >
                  {question.choices.map((choice, index) => (
                    <Stack
                      key={`choice_${question.id}_${index}`}
                      direction='row'
                      spacing={2}
                    >
                      <Typography>{choice.value}</Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </>
          )}
          <Typography
            variant='caption'
            color='textSecondary'
            style={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <InfoOutlined sx={{ fontSize: '0.85rem' }} />
            {question.hint}
          </Typography>
        </Stack>
      </Stack>
    </Card>
  );
};

export default QuestionItem;
